import {
  DesktopDatePicker,
  DesktopDatePickerProps,
  LocalizationProvider,
} from "@mui/lab";
import AdapterDayjs from "@mui/lab/AdapterDayjs";
import { msgState } from "components";
import { SField, SFieldProps } from "components/parts";
import ja from "dayjs/locale/ja";
import { getFormError } from "messages";
import { useEffect } from "react";
import {
  useController,
  UseControllerProps,
  useFormContext,
} from "react-hook-form";
import { useSetRecoilState } from "recoil";
import { dateAt, validDate } from "services/DateService";

/**
 * 日付フィールド
 */

/**
 * プロパティ
 */
type Props = Pick<UseControllerProps, "name" | "rules"> &
  Pick<DesktopDatePickerProps, "onAccept"> &
  Pick<
    SFieldProps,
    | "focusId"
    | "label"
    | "required"
    | "index"
    | "align"
    | "gridColumn"
    | "gridRow"
    | "disabled"
    | "onBlur"
    | "onChange"
    | "onKeyDown"
    | "onClick"
    | "onDoubleClick"
  >;

/**
 * 日付フィールド
 *
 * @param name: フィールド名
 * @param rules: react-hook-formのルールを設定(必須はrequiredで指定する)
 * @param label: エラーメッセージ用のラベル
 * @param required: 必須
 * @param index: フォーカス番号(1～)
 * @param align: 文字位置
 * @param gridColumn: CSSグリッドの列位置を指定する
 * @param gridRow: CSSグリッドの行位置を指定する
 * @param disabled: 非活性
 * @returns JSX.Element
 */
export const SDateField = ({
  name,
  rules,
  label,
  required,
  onAccept,
  ...props
}: Props): JSX.Element => {
  // フォーム設定
  const { control, trigger } = useFormContext();
  const { field, fieldState } = useController({
    name,
    control,
    rules: {
      ...rules,
      required: required && {
        value: required,
        message: getFormError("required", label ?? ""),
      },
      pattern: {
        value: /^[0-9]{4}(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])$/,
        message: getFormError("date", label!),
      },
    },
  });

  // エラーメッセージ設定
  const setMsg = useSetRecoilState(msgState);

  useEffect(() => {
    setMsg(fieldState.error?.message ?? "");
  }, [fieldState.error?.message, setMsg]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} locale={ja}>
      <DesktopDatePicker
        value={field.value}
        disabled={props.disabled}
        onAccept={(date) => {
          const dateString = getDateString(date, undefined);
          field.onChange(dateString);
          trigger(name);
          onAccept?.call(null, dateString);
        }}
        onChange={(date, value) => {
          const dateString = getDateString(date, value);
          field.onChange(dateString);
        }}
        mask="____/__/__"
        renderInput={({ inputRef, inputProps, InputProps }) => (
          <SField
            {...props}
            type="tel"
            value={field.value}
            inputRef={inputRef}
            formRef={field.ref}
            error={fieldState.invalid}
            onBlur={(event) => {
              trigger(name);
              props.onBlur?.call(this, event);
            }}
            InputProps={
              props.disabled
                ? undefined
                : {
                    ...InputProps,
                    endAdornment: InputProps?.endAdornment,
                  }
            }
            inputProps={{ ...inputProps, placeholder: "" }}
          />
        )}
      />
    </LocalizationProvider>
  );
};

const getDateString = (date: any, value: string | undefined): string => {
  if (date === null && typeof value === "undefined") return "";
  if (typeof value !== "undefined" && validDate(value)) return dateAt(value);
  if (validDate(date)) return dateAt(date);
  return "Invalid Date";
};
